import type { CSSProperties, ReactNode } from 'react'
import './app-state.css'

export type AppStatusStateTone = 'empty' | 'error' | 'info' | 'loading'

export interface AppStatusStateProps {
  readonly className?: string
  readonly isLoading?: boolean
  readonly message?: ReactNode
  readonly minHeight?: number | string
  readonly title: ReactNode
  readonly tone?: AppStatusStateTone
}

const statusTokens: Record<
  AppStatusStateTone,
  {
    readonly accent: string
    readonly background: string
    readonly mark: string
  }
> = {
  empty: {
    accent: 'var(--lms-color-brand-primary)',
    background: 'linear-gradient(180deg, var(--lms-color-surface-warm), var(--lms-color-surface-card))',
    mark: 'var(--lms-color-brand-primary)',
  },
  error: {
    accent: 'var(--lms-color-danger)',
    background: 'linear-gradient(180deg, color-mix(in srgb, var(--lms-color-danger) 10%, var(--lms-color-surface-card)), var(--lms-color-surface-card))',
    mark: 'var(--lms-color-danger)',
  },
  info: {
    accent: 'var(--lms-color-brand-action)',
    background: 'linear-gradient(180deg, var(--lms-color-surface-warm), var(--lms-color-surface-card))',
    mark: 'var(--lms-color-brand-action)',
  },
  loading: {
    accent: 'var(--lms-color-brand-action)',
    background: 'linear-gradient(180deg, var(--lms-color-surface-cream), var(--lms-color-surface-card))',
    mark: 'var(--lms-color-brand-action)',
  },
}

export function AppStatusState({
  className,
  isLoading = false,
  message,
  minHeight = 'auto',
  title,
  tone = isLoading ? 'loading' : 'info',
}: AppStatusStateProps) {
  const tokens = statusTokens[tone]
  const role = tone === 'error' ? 'alert' : 'status'
  const style = {
    '--app-status-accent': tokens.accent,
    '--app-status-background': tokens.background,
    '--app-status-mark': tokens.mark,
    minHeight,
  } as CSSProperties

  return (
    <section
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className={className ? `appStatusState ${className}` : 'appStatusState'}
      data-tone={tone}
      role={role}
      style={style}
    >
      <div className="appStatusStateContent">
        <div aria-hidden="true" className="appStatusStateMark">
          {isLoading ? (
            <span className="appStatusStateSpinner" />
          ) : (
            <span className="appStatusStateDot" />
          )}
        </div>
        <div className="appStatusStateCopy">
          <h2>{title}</h2>
          {message ? (
            <div className="appStatusStateMessage">{message}</div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
